import React from "react";
import "./ReportTableSection.scss";
import { createStatisticsFilterStore } from "../Stores/statisticsFilterStoreFactory";
import {
  CourtOccupancyRow,
  GuestOccupancyRow,
  MemberTypeOccupancyRow,
  RehabOccupancyRow,
  CourtDailyOccupancyRow,
  MemberOccupancyRow,
  NoShowOccupancyRow,
  ActivityOccupancyRow,
  ActivityDayCell,
  MemberTypeCount,
  CoachOccupancyRow,
} from "../../../types/api";

export type ColumnDef = {
  key: string;
  label: string;
  align?: "left" | "center" | "right";
};

export type TableCellValue =
  | string
  | number
  | null
  | {
      booker: number | null;
      participant: number | null;
      activity: number | null;
      filteredBookings?: number | null;
      systemBookings?: number | null;
      systemCapacity?: number | null;
    }
  | {
      onlyGuest: number | null;
      total: number | null;
    }
  | {
      filteredBookings: number | null;
      systemBookings: number | null;
      systemCapacity: number | null;
    };

export type TableRow = {
  type: "row" | "calc" | "total" | "summary" | "total_summary";
  hasSeparator?: boolean;
  data: Record<string, TableCellValue>;
};

interface ReportTableSectionProps {
  title: string;
  columns: ColumnDef[];
  rows: (
    | MemberTypeOccupancyRow
    | GuestOccupancyRow
    | CourtOccupancyRow
    | RehabOccupancyRow
    | CourtDailyOccupancyRow
    | MemberOccupancyRow
    | NoShowOccupancyRow
    | ActivityOccupancyRow
    | (CoachOccupancyRow & { coachName: string })
  )[];
  useCalcType?: boolean;
  useActivityType?: boolean;
  useToggleType?: boolean;
  useSelectCoach?: boolean;
  onToggleType?: (isPie: boolean) => void;
  useStore: ReturnType<typeof createStatisticsFilterStore>;
  isLoading: boolean;
  children?: React.ReactNode;
  folded?: boolean;
}

export function ReportTableSection({
  title,
  columns,
  rows,
  useCalcType = false,
  useToggleType = false,
  useSelectCoach = false,
  onToggleType,
  useStore,
  useActivityType = false,
  isLoading,
  children,
  folded = false,
}: ReportTableSectionProps) {
  const { calcType, setCalcType, typeOfBookings } = useStore();
  const [toggleType, setToggleType] = React.useState<"capacity" | "bookings">(
    "capacity"
  );

  React.useEffect(() => {
    if (onToggleType) {
      // true = pie, false = line (or whatever logic you want)
      onToggleType(toggleType === "bookings");
    }
  }, [toggleType, onToggleType]);

  return (
    <div className="report-table-section text-m">
      <div className="report-table-header">
        <span className="report-table-title">{title}</span>

        {useCalcType && (
          <div className="calc-type-toggle">
            <button
              className={`toggle-button ${
                calcType === "percent" ? "active" : ""
              }`}
              onClick={() => setCalcType("percent")}
            >
              Procent
            </button>
            <button
              className={`toggle-button ${
                calcType === "amount" ? "active" : ""
              }`}
              onClick={() => setCalcType("amount")}
            >
              Antal
            </button>
          </div>
        )}

        {useToggleType && (
          <div className="calc-type-toggle">
            <button
              className={`toggle-button ${
                toggleType === "bookings" ? "active" : ""
              }`}
              onClick={() => setToggleType("bookings")}
            >
              Av tot. bokningar
            </button>
            <button
              className={`toggle-button ${
                toggleType === "capacity" ? "active" : ""
              }`}
              onClick={() => setToggleType("capacity")}
            >
              Av bokn. kapacitet
            </button>
          </div>
        )}
        {/* {useActivityType && (
          <div className="calc-type-toggle">
            <button
              className={`toggle-button ${
                activityType === "participants" ? "active" : ""
              }`}
              onClick={() => setActivityType("participants")}
            >
              Deltagare
            </button>
            <button
              className={`toggle-button ${
                activityType === "waitlist" ? "active" : ""
              }`}
              onClick={() => setActivityType("waitlist")}
            >
              Väntelista
            </button>
          </div>
        )} */}
      </div>
      {children && <div className="children-below-title">{children}</div>}
      {!folded && (
        <table className="report-table secondary-text">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.key} style={{ textAlign: col.align || "left" }}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {rows &&
              rows.length > 0 &&
              rows.map((row, idx) => {
                const lastSummaryIndex = rows
                  .map((r) => r.type)
                  .lastIndexOf("summary");
                const isLastSummary =
                  row.type === "summary" && idx === lastSummaryIndex;

                return (
                  <tr
                    key={idx}
                    className={[
                      row.type === "calc" && "calc-row",
                      row.type === "total_summary" && "total-row",
                      (row.type === "calc" ||
                        row.type === "total_summary" ||
                        isLastSummary) &&
                        "separator-row",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    {columns.map((col) => {
                      const value = (row as any)[col.key];

                      let display: React.ReactNode = "";

                      if (
                        value &&
                        typeof value === "object" &&
                        "booker" in value &&
                        "participant" in value &&
                        "activity" in value &&
                        (value.booker != null ||
                          value.participant != null ||
                          value.activity != null)
                      ) {
                        isLoading
                          ? (display = <div className="skeleton" />)
                          : (display = (
                              <div className="structured-cell">
                                <div className="cell-first">
                                  {typeOfBookings.includes("BOOKER") && (
                                    <span className="tooltip">
                                      {Math.round(value.booker * 100) / 100}
                                      <span className="tooltip-box">
                                        Bokare
                                      </span>
                                    </span>
                                  )}
                                </div>

                                <div className="cell-divider">
                                  <div className="cell-second">
                                    {typeOfBookings.includes("CO_PLAYER") && (
                                      <span className="tooltip">
                                        {Math.round(value.participant * 100) /
                                          100}
                                        <span className="tooltip-box">
                                          Medspelare
                                        </span>
                                      </span>
                                    )}
                                  </div>

                                  <div className="cell-third">
                                    {typeOfBookings.includes("ACTIVITY") && (
                                      <span className="tooltip">
                                        {Math.round(value.activity * 100) / 100}
                                        <span className="tooltip-box">
                                          Aktivitetsbokningar
                                        </span>
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ));
                      } else if (
                        value &&
                        typeof value === "object" &&
                        "onlyGuest" in value &&
                        "total" in value &&
                        (value.onlyGuest != null || value.total != null)
                      ) {
                        if (isLoading) {
                          display = <div className="skeleton" />;
                        } else {
                          const onlyGuest = value.onlyGuest ?? 0;
                          const total = value.total ?? 0;
                          const pct =
                            total > 0
                              ? Math.round((onlyGuest / total) * 10000) / 100
                              : null;

                          // For summary & total_summary rows, show "x / y" and percentage
                          if (
                            row.type === "summary" ||
                            row.type === "total_summary"
                          ) {
                            display = (
                              <div className="structured-cell">
                                <div className="cell-first">
                                  <span className="tooltip">
                                    {`${Math.round(onlyGuest * 100) / 100} / ${
                                      Math.round(total * 100) / 100
                                    }`}
                                    <span className="tooltip-box">
                                      Endast gäster / Totalt
                                    </span>
                                  </span>
                                </div>
                                <div className="cell-third">
                                  <span className="tooltip">
                                    {pct != null ? `${pct}%` : ""}
                                    <span className="tooltip-box">
                                      Andel endast gäster
                                    </span>
                                  </span>
                                </div>
                              </div>
                            );
                          } else {
                            // For normal rows → keep the simpler numeric display
                            display = (
                              <div className="structured-cell">
                                <div className="cell-first">
                                  <span className="tooltip">
                                    {Math.round(onlyGuest * 100) / 100}
                                    <span className="tooltip-box">
                                      Endast gäster
                                    </span>
                                  </span>
                                </div>
                                <div className="cell-divider">
                                  <div className="cell-second"></div>
                                  <div className="cell-third">
                                    <span className="tooltip">
                                      {Math.round(total * 100) / 100}
                                      <span className="tooltip-box">
                                        Totalt
                                      </span>
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                        }
                      } else if (
                        value &&
                        typeof value === "object" &&
                        "filteredBookings" in value &&
                        "systemBookings" in value &&
                        "systemCapacity" in value
                      ) {
                        // summary rows
                        const {
                          filteredBookings,
                          systemBookings,
                          systemCapacity,
                        } = value;

                        const isNoData =
                          filteredBookings == null &&
                          systemBookings == null &&
                          systemCapacity == null;

                        if (isNoData) {
                          display = "";
                        } else if (isLoading) {
                          display = <div className="skeleton" />;
                        } else {
                          const fb = filteredBookings ?? 0;
                          const sb = systemBookings ?? 0;
                          const sc = systemCapacity ?? 0;

                          const denominator =
                            toggleType === "bookings" ? sb : sc;

                          const pct =
                            denominator > 0
                              ? Math.round((fb / denominator) * 10000) / 100
                              : null;

                          display = (
                            <div className="structured-cell">
                              <div className="cell-first">
                                <span className="tooltip">
                                  {toggleType === "bookings"
                                    ? `${Math.round(fb * 100) / 100} / ${
                                        Math.round(sb * 100) / 100
                                      }`
                                    : `${Math.round(fb * 100) / 100} / ${
                                        Math.round(sc * 100) / 100
                                      }`}
                                  <span className="tooltip-box">
                                    {toggleType === "bookings"
                                      ? "Bokade / Tot. bokningar"
                                      : "Bokade / Bokningsbara"}
                                  </span>
                                </span>
                              </div>
                              <div className="cell-third">
                                <span className="tooltip">
                                  {pct != null ? `${pct}%` : ""}
                                  <span className="tooltip-box">
                                    Utnyttjande
                                  </span>
                                </span>
                              </div>
                            </div>
                          );
                        }
                      } else if (
                        typeof value === "string" &&
                        row.__typename === "CourtOccupancyRow" &&
                        [
                          "utfall_23",
                          "utfall_09",
                          "one_day_09",
                          "two_days_09",
                          "three_days_09",
                          "four_days_09",
                          "five_days_09",
                        ].includes(col.key)
                      ) {
                        const totalSlots = (row as any).total_slots ?? 0;

                        // extract numerator from the "x/total" string
                        const [bookedStr] = value.split("/");
                        const booked = parseFloat(bookedStr) || 0;
                        const pct =
                          totalSlots > 0
                            ? Math.round((booked / totalSlots) * 10000) / 100
                            : null;

                        display = (
                          <div className="structured-cell">
                            <div className="cell-first">
                              <span className="tooltip">
                                {`${booked} / ${totalSlots}`}
                                <span className="tooltip-box">
                                  Bokade / Totala
                                </span>
                              </span>
                            </div>
                            <div className="cell-third">
                              <span className="tooltip">
                                {pct != null ? `${pct}%` : ""}
                                <span className="tooltip-box">Utnyttjande</span>
                              </span>
                            </div>
                          </div>
                        );
                      } else if (
                        row.__typename === "CourtDailyOccupancyRow" &&
                        value &&
                        typeof value === "object" &&
                        "booked" in value &&
                        "blue" in value &&
                        "red" in value
                      ) {
                        // --- Handle new daily court occupancy cells ---
                        const day = value as any;
                        const { total_slots, booked, blue, red } = day;

                        // user’s calcType toggle (percent / amount)
                        const showPercent = calcType === "percent";

                        if (isLoading) {
                          display = <div className="skeleton" />;
                        } else {
                          display = (
                            <div className="structured-cell court-daily-cell">
                              <div className="cell-first">
                                <span className="tooltip">
                                  {showPercent
                                    ? `${booked.percent}%`
                                    : `${booked.amount}/${total_slots}`}
                                  <span className="tooltip-box">
                                    Bokade / Totalt
                                  </span>
                                </span>
                              </div>
                              <div className="cell-divider">
                                <div className="cell-second blue-time">
                                  <span className="tooltip">
                                    {showPercent
                                      ? `${blue.percent}%`
                                      : blue.amount}
                                    <span className="tooltip-box">
                                      Blå priszon
                                    </span>
                                  </span>
                                </div>
                                <div className="cell-third red-time">
                                  <span className="tooltip">
                                    {showPercent
                                      ? `${red.percent}%`
                                      : red.amount}
                                    <span className="tooltip-box">
                                      Röd priszon
                                    </span>
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        }
                      } else if (
                        row.__typename === "ActivityOccupancyRow" &&
                        value &&
                        typeof value === "object" &&
                        "booked" in value &&
                        "capacity" in value &&
                        (value.booked != null || value.capacity != null)
                      ) {
                        const day = value as any;
                        const {
                          capacity,
                          booked,
                          memberTypes,
                          waitlistCount,
                          waitlistMemberTypes,
                        } = day;

                        if (isLoading) {
                          display = <div className="skeleton" />;
                        } else {
                          display = (
                            <div className="structured-cell court-daily-cell">
                              <div className="cell-first">
                                <span className="tooltip">
                                  {booked}/{capacity}
                                  <span className="tooltip-box">
                                    Bokade / Tillgängliga
                                    {memberTypes.map((mt: any) => (
                                      <div key={mt.memberType}>
                                        {mt.memberType}: {mt.count}
                                      </div>
                                    ))}
                                  </span>
                                </span>
                              </div>
                              <div className="cell-divider">
                                <div className="cell-second">
                                  <span className="tooltip">
                                    {waitlistCount}
                                    <span className="tooltip-box">
                                      Väntelista
                                      {waitlistMemberTypes &&
                                        waitlistMemberTypes.map(
                                          (wmt: MemberTypeCount) => (
                                            <div key={wmt.memberType}>
                                              {wmt.memberType}: {wmt.count}
                                            </div>
                                          )
                                        )}
                                    </span>
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        }
                      } else if (
                        row.__typename === "ActivityOccupancyRow" &&
                        value &&
                        typeof value === "object" &&
                        "fullyBookedSessions" in value &&
                        "totalSessions" in value &&
                        (value.fullyBookedSessions != null ||
                          value.totalSessions != null)
                      ) {
                        const day = value as any;
                        const {
                          fullyBookedSessions,
                          totalSessions,
                          waitlistCount,
                          waitlistMemberTypes,
                        } = day;

                        if (isLoading) {
                          display = <div className="skeleton" />;
                        } else {
                          display = (
                            <div className="structured-cell court-daily-cell">
                              <div className="cell-first">
                                <span className="tooltip">
                                  {fullyBookedSessions}/{totalSessions}
                                  <span className="tooltip-box">
                                    Fullbokade / Totalt antal
                                  </span>
                                </span>
                              </div>
                              <div className="cell-divider">
                                <div className="cell-second">
                                  <span className="tooltip">
                                    {waitlistCount}
                                    <span className="tooltip-box">
                                      Väntelista
                                      {waitlistMemberTypes &&
                                        waitlistMemberTypes.map(
                                          (wmt: MemberTypeCount) => (
                                            <div key={wmt.memberType}>
                                              {wmt.memberType}: {wmt.count}
                                            </div>
                                          )
                                        )}
                                    </span>
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        }
                      } else if (
                        row.__typename === "CoachOccupancyRow" &&
                        value &&
                        typeof value === "object" &&
                        "booked" in value &&
                        "memberTypes" in value &&
                        (value.booked != null || value.memberTypes != null)
                      ) {
                        const day = value as any;
                        const { booked, memberTypes } = day;

                        if (isLoading) {
                          display = <div className="skeleton" />;
                        } else {
                          display = (
                            <div className="structured-cell court-daily-cell">
                              <div className="cell-first">
                                <span className="tooltip">
                                  {booked}
                                  <span className="tooltip-box">
                                    Bokade:
                                    {memberTypes &&
                                      memberTypes.map(
                                        (wmt: MemberTypeCount) => (
                                          <div key={wmt.memberType}>
                                            {wmt.memberType}: {wmt.count}
                                          </div>
                                        )
                                      )}
                                  </span>
                                </span>
                              </div>
                            </div>
                          );
                        }
                      } else {
                        if (col.key === "week") {
                          display = value ? `v. ${value}` : "";
                        } else if (col.key === "percentage_occupancy") {
                          const available = (row as any).available_count ?? 0;
                          const booked = (row as any).bookings_count ?? 0;
                          const pct =
                            available > 0
                              ? Math.round((booked / available) * 10000) / 100
                              : null;

                          display = (
                            <div className="structured-cell">
                              <div className="cell-first">
                                <span className="tooltip">
                                  {pct != null ? `${pct}%` : ""}
                                  <span className="tooltip-box">
                                    Andel bokade av tillgängliga
                                  </span>
                                </span>
                              </div>
                            </div>
                          );
                        } else if (value && typeof value === "object") {
                          display = "";
                        } else {
                          display =
                            typeof value === "number"
                              ? Math.round(value * 100) / 100
                              : value ?? "";
                        }
                      }

                      return (
                        <td
                          key={col.key}
                          style={{ textAlign: col.align || "left" }}
                        >
                          {display}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
          </tbody>
        </table>
      )}
    </div>
  );
}
